import { useState } from 'react';
import { ListOrdered, Plus, Trash2, FileText, FileSpreadsheet, Ruler, Download } from 'lucide-react';
import { getMode } from '../lib/profiles';
import { toast } from 'sonner';
import useCatalog from '../hooks/useCatalog';
import { UNITS, inSector } from '../lib/catalog';
import { money } from '../lib/units';
import { askConfirm } from '../lib/askConfirm';
import { uid } from '../lib/model';
import { oz, lvAmount, lvSources, exportLvPdf, exportLvXLSX, lvTotal } from '../lib/lv';

function Pos({ t, p, si, pi, src, set, del }) {
  return (
    <div className="lv-pos" data-testid={`lv-pos-${pi}`}>
      <b className="lv-oz mono" data-testid={`lv-oz-${pi}`}>{oz(si, pi)}</b>
      <textarea rows={2} data-testid={`lv-text-${pi}`} value={p.text} placeholder={t.lvText} onChange={(e) => set({ text: e.target.value })} />
      <div className="lv-nums">
        <label>{t.rmQty}<input inputMode="decimal" data-testid={`lv-qty-${pi}`} value={p.qty ?? ''} onChange={(e) => set({ qty: e.target.value, src: null })} /></label>
        <label>{t.mfColUnit}<select data-testid={`lv-unit-${pi}`} value={p.unit} onChange={(e) => set({ unit: e.target.value })}>{UNITS.map((u) => <option key={u} value={u}>{t['unit_' + u]}</option>)}</select></label>
        <label>{t.lvEp}<input inputMode="decimal" data-testid={`lv-price-${pi}`} value={p.price ?? ''} onChange={(e) => set({ price: e.target.value })} /></label>
        <label>{t.lvGp}<output data-testid={`lv-amount-${pi}`}>{money(lvAmount(p))}</output></label>
        <button className="icon-btn danger lv-del" data-testid={`lv-del-${pi}`} onClick={del}><Trash2 size={14} /></button>
      </div>
      {src.length > 0 && (
        <label className="lv-src"><Ruler size={13} />
          <select data-testid={`lv-src-${pi}`} value={p.src || ''} onChange={(e) => { const s = src.find((x) => x.id === e.target.value); if (s) { set({ qty: String(s.qty).replace('.', ','), unit: s.unit, src: s.id, ...(s.price != null ? { price: String(s.price).replace('.', ',') } : {}) }); toast.success(t.lvSrcDone); } }}>
            <option value="">{t.lvSrcPick}</option>
            {src.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
      )}
    </div>
  );
}

// Leistungsverzeichnis of one building section: numbered positions (catalog or own) with qty, unit, unit price
export default function LvBox({ t, project, report, updateReport }) {
  const cat = useCatalog();
  const [pick, setPick] = useState('');
  const list = report.lv || [], si = project.reports.findIndex((r) => r.id === report.id);
  const save = (lv) => updateReport({ lv });
  const items = cat.items.filter((x) => inSector(x, getMode() === 'building' ? 'building' : 'net'));
  const src = lvSources(project, report, t);
  const add = (p) => save([...list, { id: uid('lv'), text: '', qty: '', unit: 'm2', price: '', ...p }]);
  const fromCat = (id) => { const x = items.find((i) => i.id === id), pr = project.prices?.items?.[id]?.price ?? x?.price; if (x) add({ text: [x.name, x.dim].filter(Boolean).join(' · '), unit: x.unit || 'pcs', price: pr != null ? String(pr).replace('.', ',') : '', ref: x.id }); setPick(''); };
  // all pipe lengths, parts and pits not yet in this section become positions (quantity + project price)
  const importAll = () => {
    const have = new Set(list.map((p) => p.src)), add2 = src.filter((x) => /^(net|pit):/.test(x.id) && !have.has(x.id));
    if (!add2.length) { toast.message(t.lvImportNone); return; }
    save([...list, ...add2.map((x) => ({ id: uid('lv'), text: x.text || x.label, qty: String(x.qty).replace('.', ','), unit: x.unit, price: x.price != null ? String(x.price).replace('.', ',') : '', src: x.id }))]);
    toast.success(t.lvImportDone.replace('{n}', add2.length));
  };
  const sum = list.reduce((a, p) => a + lvAmount(p), 0);
  return (
    <div className="lv-box" data-testid="lv-box">
      <div className="sub-title"><ListOrdered size={14} /> {t.lvTitle} – {String(si + 1).padStart(2, '0')} {report.title}</div>
      {list.length ? list.map((p, pi) => <Pos key={p.id} t={t} p={p} si={si} pi={pi} src={src} set={(o) => save(list.map((x) => (x.id === p.id ? { ...x, ...o } : x)))} del={async () => (await askConfirm(t.lvDelQ, { ok: t.pitDelete, cancel: t.btnCancel })) && save(list.filter((x) => x.id !== p.id))} />)
        : <div className="empty" data-testid="lv-empty">{t.lvEmpty}</div>}
      <div className="row lv-actions">
        <select data-testid="lv-cat-select" value={pick} onChange={(e) => fromCat(e.target.value)}>
          <option value="">{t.lvFromCat}</option>
          {items.map((x) => <option key={x.id} value={x.id}>{x.name}{x.dim ? ` · ${x.dim}` : ''}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" data-testid="lv-add-btn" onClick={() => add({})}><Plus size={14} /> {t.rmAdd}</button>
        {src.some((x) => /^(net|pit):/.test(x.id)) && <button className="btn btn-green btn-sm" data-testid="lv-import-btn" onClick={importAll}><Download size={14} /> {t.lvImport}</button>}
      </div>
      <div className="lv-sum" data-testid="lv-sec-sum">{t.lvSecSum} {String(si + 1).padStart(2, '0')}: <b>{money(sum)}</b> · {t.lvAll}: <b data-testid="lv-total">{money(lvTotal(project))}</b></div>
      <div className="row">
        <button className="btn btn-dark btn-sm" data-testid="lv-pdf-btn" disabled={!lvTotal(project) && !project.reports.some((r) => (r.lv || []).length)} onClick={() => exportLvPdf(project, t)}><FileText size={14} /> {t.lvPdf}</button>
        <button className="btn btn-green btn-sm" data-testid="lv-xlsx-btn" disabled={!project.reports.some((r) => (r.lv || []).length)} onClick={() => exportLvXLSX(project, t)}><FileSpreadsheet size={14} /> {t.lvXlsx}</button>
      </div>
    </div>
  );
}
